import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faPlus, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

import { getStageList } from "../api/stageService";
import { Stage } from "../types/stage.types";

import "../styles/StageList.css";

const StageList: React.FC = () => {
    const navigate = useNavigate();

    const [stages, setStages] = useState<Stage[]>([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState<number | null>(null);
    const [nextPage, setNextPage] = useState<string | null>(null);
    const [prevPage, setPrevPage] = useState<string | null>(null);
    const [openActionId, setOpenActionId] = useState<number | null>(null);
    const [filters, setFilters] = useState({
        stage_name: "",
        description: "",
        is_active: "true", // default active
    });


    const [ordering, setOrdering] = useState("");
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [debouncedFilters, setDebouncedFilters] = useState(filters);
    const [isFetching, setIsFetching] = useState(false);
    const getSortIcon = (field: string) => {
        if (ordering === field) return faSortUp;
        if (ordering === `-${field}`) return faSortDown;
        return faSort;
    };

    const isFilterChange = React.useRef(false);
    const toggleOrdering = (field: string) => {
        setOrdering(prev => {
            if (prev === field) return `-${field}`;
            if (prev === `-${field}`) return "";
            return field;
        });
    };

    /* =========================
       FETCH STAGES
    ========================== */

    useEffect(() => {
  const handler = setTimeout(() => {
    isFilterChange.current = true;   // ✅ mark filter change
    setDebouncedFilters(filters);
    setPage(1);
  }, 500);

  return () => clearTimeout(handler);
}, [filters]);
    useEffect(() => {
        fetchStages(page, debouncedFilters);
    }, [page, ordering, debouncedFilters]);



    const fetchStages = async (pageNo: number, appliedFilters = filters) => {
       if (!isInitialLoad && !isFilterChange.current) {
  setIsFetching(true);
}

        try {
            const res = await getStageList(pageNo, {
                stage_name__icontains: appliedFilters.stage_name,
                description__icontains: appliedFilters.description,
                is_active: appliedFilters.is_active,
                ordering,
            });

            let results: Stage[] = res.data.results || [];

            // ✅ SORT BY SEQUENCE
            results = results.sort((a, b) => a.sequence - b.sequence);

            setStages(results);
            setTotalCount(res.data.count || 0);
            setNextPage(res.data.next);
            setPrevPage(res.data.previous);

            if (pageSize === null && results.length) {
                setPageSize(results.length);
            }
        } finally {
            setIsFetching(false);
            setIsInitialLoad(false);
            isFilterChange.current = false;
        }
    };




    const totalPages =
        pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;

    /* =========================
       CLOSE ACTION DROPDOWN
    ========================== */
    useEffect(() => {
        const closeDropdown = () => setOpenActionId(null);
        document.addEventListener("click", closeDropdown);
        return () => document.removeEventListener("click", closeDropdown);
    }, []);

    return (
        <>
            {/* PAGE HEADER */}
            <PageHeader
                title="Stage List"
                breadcrumbs={[
                    { label: "Home", path: "/dashboard" },
                    { label: "Masters" },
                    { label: "Stages" },
                ]}
            />

            <div className="container mt-4">
                <div className="d-flex justify-content-end mb-3">
                    {/* CREATE STAGE BUTTON */}
                    <button
                        className="btn btn-primary "
                        onClick={() => navigate("/stage/create")}
                    >
                        <FontAwesomeIcon icon={faPlus} /> Add Stage
                    </button>
                </div>
                {/* TABLE CARD */}
                <div className="hr-card">

                    {/* 🔹 FIRST LOAD */}
                    {isInitialLoad && (
                        <div className="skeleton-table" />
                    )}

                    {/* 🔹 SUBSEQUENT FETCH (spinner overlay) */}
                    {isFetching && (
                        <div className="table-loading-overlay">
                            <span className="spinner-border spinner-border-sm" />
                        </div>
                    )}

                    {/* 🔹 TABLE */}
                    {!isInitialLoad && (
                        <>

                            <table className="table hr-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "center" }}>Action</th>
                                        <th
                                            className="sortable"
                                            onClick={() => toggleOrdering("stage_name")}
                                        >
                                            Stage Name <FontAwesomeIcon icon={getSortIcon("stage_name")} />
                                        </th>
                                        <th
                                            className="sortable"
                                            onClick={() => toggleOrdering("description")}
                                        >
                                            Description <FontAwesomeIcon icon={getSortIcon("description")} />
                                        </th>

                                        <th
                                            className="sortable"
                                            onClick={() => toggleOrdering("is_active")}
                                        >
                                            Status <FontAwesomeIcon icon={getSortIcon("is_active")} />
                                        </th>
                                    </tr>
                                    {/* FILTER ROW */}
                                    <tr className="table-filter-row">
                                        <th></th>

                                        <th>
                                            <input
                                                className="form-control form-control-sm"
                                                placeholder="Search"
                                                value={filters.stage_name}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, stage_name: e.target.value })
                                                }
                                            />
                                        </th>

                                        <th>
                                            <input
                                                className="form-control form-control-sm"
                                                placeholder="Search"
                                                value={filters.description}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, description: e.target.value })
                                                }
                                            />
                                        </th>

                                        <th>
                                            <select
                                                className="form-select form-select-sm"
                                                value={filters.is_active}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, is_active: e.target.value })
                                                }
                                            >
                                                <option value="">All</option>
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </select>
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {stages.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center">
                                                No stages found
                                            </td>
                                        </tr>
                                    ) : (
                                        stages.map((stage) => (
                                            <tr key={stage.id}>
                                                {/* ACTION */}
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        position: "relative",
                                                    }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="action-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenActionId(
                                                                openActionId === stage.id ? null : stage.id
                                                            );
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faEllipsisV} />
                                                    </button>

                                                    {openActionId === stage.id && (
                                                        <div
                                                            className="action-dropdown"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setOpenActionId(null);
                                                                    navigate(`/stage/edit/${stage.id}`);
                                                                }}
                                                            >
                                                                Edit
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="view"
                                                                onClick={() => {
                                                                    setOpenActionId(null);
                                                                    navigate(`/stage/view/${stage.id}`);
                                                                }}
                                                            >
                                                                View
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>

                                                {/* DATA */}
                                                <td>{stage.stage_name}</td>
                                                <td>{stage.description || "-"}</td>

                                                {/* STATUS */}
                                                <td>
                                                    <span
                                                        className={`status-badge ${stage.is_active ? "active" : "inactive"
                                                            }`}
                                                    >
                                                        {stage.is_active ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>

                            {/* PAGINATION */}
                            <div className="pagination d-flex gap-1 justify-content-end">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={!prevPage}
                                    onClick={() => setPage(page - 1)}
                                >
                                    Prev
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                                    (p) => (
                                        <button
                                            key={p}
                                            className={`btn btn-sm ${p === page
                                                ? "btn-primary"
                                                : "btn-outline-secondary"
                                                }`}
                                            onClick={() => setPage(p)}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}

                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={!nextPage}
                                    onClick={() => setPage(page + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default StageList;
